/**
 * **A08 · The key log** (Stream A, Paper.dc.html), laid out by the `key_log` engine from Festival
 * Ops → Radios & keys, the same custody list the admin station reads at Close the booths: every
 * key on the ring, who has it today, out and back, and who handed it over. Scored by walk 99g.
 */
import type { ReactNode } from 'react';
import { FestivalClock, keyLog } from '@cider/domain/engines';
import { formatTime } from '@cider/ui';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

interface Line {
  id: string;
  item: string;
  itemName: string;
  number: string;
  name: string;
  issuedByName: string;
  issuedAt: string;
  returnedAt: string | null;
}

/** `/print/key-log/<year>[:<operating day>]`: today's operating day by default. */
export function KeyLog({ year, day }: { year: string; day?: string | undefined }): ReactNode {
  const d = useLoad<Line[]>(`/ops/custody?year=${year}`);
  usePrinted('key-log', !!d.data, { year: Number(year) });
  if (d.error) return <p role="alert">{d.error}</p>;
  if (!d.data) return <p>Reading the keys…</p>;
  const on = day ?? FestivalClock.operatingDay(new Date());
  const page = keyLog(
    on,
    d.data
      .filter((x) => x.item === 'key')
      .map((k) => ({
        key: k.itemName || k.number,
        to: k.name,
        day: FestivalClock.operatingDay(new Date(k.issuedAt)),
        out: formatTime(k.issuedAt),
        back: k.returnedAt ? formatTime(k.returnedAt) : null,
        by: k.issuedByName,
      })),
  );
  return <DrawnPages pages={[page]} stamp="time" />;
}
